import * as React from 'react';
import { Api } from './Api';
import { ApiAtomContent, createApiAtom } from './Atom';
import { apiFetch } from './apiFetch';
import { App } from 'InternalLibrary/app/App';
import { Atom } from 'jotai/vanilla/atom';
import { FetchApi } from './FetchApi';
import { GetEndpointMethodNames } from './GetEndpointMethodNames';
import type { SetStateAction } from 'jotai';

export type ApiContextProvider = (
  props: React.PropsWithChildren,
) => JSX.Element;

export type Atoms = {
  [key: string]: Atom<ApiAtomContent<any, any, any>>;
};

export interface ApiContextValue<TApi extends Api<any, any>> {
  atoms: Atoms;
  setAtom: (key: string, atom: Atom<ApiAtomContent<any, any, any>>) => void;
  removeAtom: (key: string) => void;
  getAtom: <
    TPath extends TApi['Path'],
    TMethod extends GetEndpointMethodNames<TApi, TPath>,
  >(
    path: TPath,
    method: TMethod,
    key?: string,
  ) => Atom<ApiAtomContent<TApi, TPath, TMethod>>;
  createFetch: <
    TPath extends TApi['Path'],
    TMethod extends GetEndpointMethodNames<TApi, TPath>,
  >(
    path: TPath,
    method: TMethod,
    context: {
      content: ApiAtomContent<any, TPath, TMethod>;
      setContent: (
        update: SetStateAction<ApiAtomContent<any, TPath, TMethod>>,
      ) => void;
    },
  ) => FetchApi<TApi, TPath, TMethod>;
}

export const callbackFallback = () => {
  throw Error('API Context not provided');
};

type Result = ApiContextValue<any>;
const atoms: Atoms = {};
const setAtom: Result['setAtom'] = (key, data) => {
  atoms[key] = data;
};
const getAtom: Result['getAtom'] = (
  path,
  method,
  key = `${method.toString()}|${String(path)}`,
) => {
  let atom = atoms[key];

  if (!atom) {
    atom = createApiAtom(path, method);
    setAtom(key, atom);
  }

  return atom;
};
const removeAtom: Result['removeAtom'] = (key) => {
  delete atoms[key];
};

type ResolveFetch = (data: any, error?: any) => void;

const fetchListeners = new Map<string, Array<ResolveFetch>>();
const addListener = (url: string, resolve: ResolveFetch) =>
  fetchListeners.set(url, [...(fetchListeners.get(url) || []), resolve]);
const resolveListeners = (url: string, data: any) => {
  const listeners = fetchListeners.get(url);
  if (listeners) {
    listeners.forEach((resolve) => resolve(data));
    fetchListeners.delete(url);
  }
};

export function createApiContextProvider<TApp extends App<any, any>>(
  app: TApp,
): ApiContextProvider {
  return ({ children }) => {
    const { fetch } = app.useApp();

    const createFetch = React.useCallback<Result['createFetch']>(
      (path, method, { content, setContent }) => {
        return async (options = {}) => {
          setContent((oldData) => ({
            ...oldData,
            fetching: true,
            request: options as any,
          }));

          try {
            let data: any;
            const stringPath: string = String(path);
            if (fetchListeners.has(stringPath)) {
              data = await new Promise((resolve, reject) => {
                addListener(stringPath, (data, error) =>
                  error !== undefined ? reject(error) : resolve(data),
                );
              });
            } else {
              fetchListeners.set(stringPath, []);
              data = await apiFetch(path, method, options, fetch);
              resolveListeners(stringPath, data);
            }
            setContent((oldData) => ({
              ...oldData,
              fetching: false,
              data,
            }));

            return data;
          } catch (error) {
            setContent((oldData) => ({
              ...oldData,
              fetching: false,
              error,
            }));
            throw error;
          }
        };
      },
      [fetch],
    );

    return (
      <app.apiContext.Provider
        value={{
          atoms,
          setAtom,
          removeAtom,
          getAtom,
          createFetch,
        }}
      >
        {children}
      </app.apiContext.Provider>
    );
  };
}
